import { Link } from 'gatsby'
import Image from 'src/components/ui/Image/Image'
import { useMobile } from 'src/hooks/useMobile'
import { InView } from 'react-intersection-observer'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import Section from '../../Section'
import { Carousel } from '../common/Carousel'
import './style.scss'

interface DepartamentItemProps {
  imageTitle: string
  fontColor: string
  image: string
  backgroundColor: string
  imageUrl: string
}

interface BannerDepartmentsProps {
  images: DepartamentItemProps[]
  section?: string
  locationOnPage?: string
}

export default function BannerDepartments({
  images,
  section,
  locationOnPage,
}: BannerDepartmentsProps) {
  const { screenWidth } = useMobile()
  const { pageData } = useGlobalContext()
  const options = {
    className: 'bannerDepartment',
    initialSlide: 0,
    centerInsufficientSlides: true,
    breakpoints: {
      1120: {
        slidesPerView: 6,
        spaceBetween: 16,
      },
      768: {
        slidesPerView: 5,
        spaceBetween: 16,
      },
      320: {
        slidesPerView: 4,
        spaceBetween: 4,
      },
    },
  }

  const sendPromoFeatureViewedEvent = (
    item: DepartamentItemProps,
    index: number
  ) => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(pageData),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: item?.imageTitle ?? 'N/A',
        index,
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  const standardObject = getStandardObject(pageData)

  const sendAmplitudeClickEvent = (
    item: DepartamentItemProps,
    index: number
  ) => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: item.imageTitle ?? '',
      index,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })
  }

  return (
    <Section className="bannerDepartmentSection">
      <Carousel options={options}>
        {images.map((item: DepartamentItemProps, index: number) => {
          return (
            <InView
              as="div"
              key={index}
              threshold={0.7}
              onChange={(inView) => {
                if (inView) {
                  sendPromoFeatureViewedEvent(item, index)
                }
              }}
              triggerOnce
              onClick={() => sendAmplitudeClickEvent(item, index)}
              onKeyDown={() => sendAmplitudeClickEvent(item, index)}
            >
              <Link
                to={item?.imageUrl ?? '#'}
                className="bannerDepartmentItem overflow-hidden"
              >
                <p
                  style={{ color: item?.fontColor }}
                  className="departmentTitle"
                >
                  {item?.imageTitle}
                </p>
                <div style={{ backgroundColor: item?.backgroundColor }}>
                  {item?.image ? (
                    <Image
                      alt={item.imageTitle}
                      src={item.image}
                      width={screenWidth < 480 ? 80 : 210}
                      height={screenWidth < 480 ? 60 : 170}
                      loading={index < 3 ? 'eager' : 'lazy'}
                      preload={index < 3}
                      sizes="(max-width: 766px) 60px, 100vw"
                      className={index >= 3 ? 'swiper-lazy' : ''}
                    />
                  ) : (
                    <div
                      className="container"
                      style={{
                        width: screenWidth < 480 ? 80 : 210,
                        height: screenWidth < 480 ? 60 : 170,
                      }}
                    />
                  )}
                </div>
              </Link>
            </InView>
          )
        })}
      </Carousel>
    </Section>
  )
}
